import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMe, useOrgMe, useSignalDefinitions, useSignals } from "../api/hooks";
import { Chip, Empty, ErrorBox, Loading, Page, Table } from "../components/ui";
import { SignalCard } from "../components/signal-card";
import { latestByScope, toneFor, unitOf } from "../lib/signals";
import { title } from "../lib/format";

export const Route = createFileRoute("/performance")({ component: Performance });

/** Function-aware: engineering and support read the first row, sales the second. */
const throughput = ["items_closed_per_person_week", "team_cycle_time_days", "review_turnaround_hours", "overdue_items_per_person", "open_items_per_person", "open_item_age_days"];
const sales = ["deal_amount_won_week", "win_rate_pct", "deal_slippage_pct", "deal_stage_age_days"];
const personKeys = ["items_closed_week", "cycle_time_days", "overdue_work_items", "open_work_items", "goal_health_pct", "action_follow_through_pct"];

const scopes = [
  { v: "dept", l: "Departments" },
  { v: "team", l: "Teams" },
  { v: "manager", l: "Managers" },
  { v: "org", l: "Company" },
];

function Performance() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  return isAdmin ? <AdminPerformance /> : <ManagerPerformance />;
}

function AdminPerformance() {
  const [scope, setScope] = useState("dept");
  const [key, setKey] = useState<string | null>(null);
  const defs = useSignalDefinitions();
  const all = useSignals({ scope_type: scope });
  const latest = useMemo(() => (all.data ? latestByScope(all.data) : new Map()), [all.data]);
  const keys = useMemo(() => {
    const s = new Set<string>();
    for (const [k] of latest) s.add(k.split("|")[2]);
    return [...s].sort();
  }, [latest]);
  const selected = key ?? keys[0] ?? null;
  const entry = (k: string) => (selected ? latest.get(`${k}|${scope}|${selected}`) : undefined);
  return (
    <Page title="Teams and performance" sub="Company → department → team → manager. Throughput and outcomes from the work tools, aggregated behind the privacy floor.">
      <div className="flex gap-2 mb-4">
        {scopes.map((s) => (
          <button key={s.v} className={`btn ${scope === s.v ? "btn-dark" : "btn-light"} !py-2 !px-3 !text-[12.5px]`} onClick={() => { setScope(s.v); setKey(null); }}>{s.l}</button>
        ))}
      </div>
      {all.isError && <ErrorBox error={all.error} />}
      {all.isPending ? (
        <Loading />
      ) : keys.length === 0 ? (
        <Empty title="Nothing above the floor yet" body="Work signals appear after a work tool (GitHub, Jira, HubSpot, Zendesk…) has synced and the nightly job has run." />
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: "260px 1fr" }}>
          <div className="card !p-2 self-start">
            {keys.map((k) => (
              <button key={k} className={`app-nav w-full text-left ${selected === k ? "active" : ""}`} onClick={() => setKey(k)}>{k}</button>
            ))}
          </div>
          <div>
            <h3 className="text-[15px] font-bold mb-2">Throughput</h3>
            <div className="grid grid-cols-4 gap-3">
              {throughput.map((k) => <SignalCard key={k} label={title(k)} unit={unitOf(defs.data, k)} entry={entry(k)} />)}
            </div>
            <h3 className="text-[15px] font-bold mt-6 mb-2">Sales outcomes</h3>
            <div className="grid grid-cols-4 gap-3">
              {sales.map((k) => <SignalCard key={k} label={title(k).replace(" Pct", " %")} unit={unitOf(defs.data, k)} entry={entry(k)} />)}
            </div>
            <h3 className="text-[15px] font-bold mt-6 mb-2">Every {scope === "dept" ? "department" : scope === "org" ? "scope" : scope} this week</h3>
            <Table
              rows={keys}
              keyOf={(k) => k}
              cols={[
                { h: title(scope), cell: (k) => <span className="font-semibold">{k}</span> },
                ...[...throughput, ...sales].map((sig) => ({
                  h: title(sig).replace(" Pct", " %"),
                  w: "120px",
                  cell: (k: string) => {
                    const e = latest.get(`${sig}|${scope}|${k}`);
                    if (!e) return <span className="text-muted">—</span>;
                    const t = toneFor(sig, e.latest.value);
                    const v = e.latest.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + (unitOf(defs.data, sig) === "pct" ? "%" : "");
                    return t === "neutral" ? <span>{v}</span> : <Chip tone={t}>{v}</Chip>;
                  },
                })),
              ]}
            />
          </div>
        </div>
      )}
    </Page>
  );
}

function ManagerPerformance() {
  const org = useOrgMe();
  const defs = useSignalDefinitions();
  const meKey = org.data?.me?.person_key;
  const team = useSignals({ scope_type: "manager", scope_key: meKey ?? "-" });
  const people = useSignals({ scope_type: "person" });
  if (org.isPending) return <Page title="Performance"><Loading /></Page>;
  if (org.isError) return <Page title="Performance"><ErrorBox error={org.error} /></Page>;
  if (!org.data.me) return <Page title="Performance"><Empty title="Your user is not linked to a person" /></Page>;
  const t = team.data ? latestByScope(team.data) : new Map();
  const p = people.data ? latestByScope(people.data) : new Map();
  return (
    <Page title="Performance" sub={`${org.data.me.full_name ?? "Your line"}: throughput and outcomes for your reporting line, and each person in it.`}>
      <div className="grid grid-cols-4 gap-3">
        {[...throughput, ...sales].slice(0, 4).map((k) => <SignalCard key={k} label={title(k)} unit={unitOf(defs.data, k)} entry={meKey ? t.get(`${k}|manager|${meKey}`) : undefined} />)}
      </div>
      <h3 className="text-[15px] font-bold mt-6 mb-2">Your people</h3>
      <Table
        rows={org.data.direct_reports}
        keyOf={(r) => r.person_key}
        empty="No direct reports on record"
        cols={[
          { h: "Person", cell: (r) => <><span className="font-semibold">{r.full_name ?? r.person_key}</span><span className="sub">{r.title ?? ""}</span></> },
          ...personKeys.map((k) => ({
            h: title(k).replace(" Pct", " %"),
            w: "120px",
            cell: (r: { person_key: string }) => {
              const e = p.get(`${k}|person|${r.person_key}`);
              if (!e) return <span className="text-muted">—</span>;
              const tone = toneFor(k, e.latest.value);
              const v = e.latest.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + (unitOf(defs.data, k) === "pct" ? "%" : "");
              return tone === "neutral" ? <span>{v}</span> : <Chip tone={tone}>{v}</Chip>;
            },
          })),
        ]}
      />
    </Page>
  );
}
